/**
 * Gateways List
 * View and manage gateways
 */

import { useQuery } from '@tanstack/react-query';
import { fetchGateways } from './api';
import type { Gateway } from './types';

export default function GatewaysList() {
  const { data: gateways = [], isLoading, error } = useQuery({
    queryKey: ['iot', 'gateways'],
    queryFn: fetchGateways,
    refetchInterval: 15000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load gateways: {String(error)}</p>
      </div>
    );
  }

  const onlineGateways = gateways.filter(g => g.status === 'online');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gateways</h1>
          <p className="text-gray-600 mt-1">
            {gateways.length} total • {onlineGateways.length} online
          </p>
        </div>
      </div>

      {/* Gateways Grid */}
      {gateways.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Gateways Found</h3>
          <p className="text-gray-600 mb-4">Use the Agent chat to register new gateways</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gateways.map((gateway) => (
            <GatewayCard key={gateway._id} gateway={gateway} />
          ))}
        </div>
      )}
    </div>
  );
}

function GatewayCard({ gateway }: { gateway: Gateway }) {
  const statusColors = {
    online: 'bg-green-100 text-green-800',
    offline: 'bg-red-100 text-red-800',
    maintenance: 'bg-yellow-100 text-yellow-800',
    unknown: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{gateway.gatewayId}</h3>
          {gateway.manufacturer && gateway.model && (
            <p className="text-sm text-gray-600 mt-1">
              {gateway.manufacturer} {gateway.model}
            </p>
          )}
        </div>
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[gateway.status]}`}>
          {gateway.status}
        </span>
      </div>

      <div className="space-y-2">
        {gateway.type && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Type:</span>
            <span className="font-medium text-gray-900">{gateway.type}</span>
          </div>
        )}

        {gateway.firmware?.current && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Firmware:</span>
            <span className="font-mono text-xs text-gray-900">{gateway.firmware.current}</span>
          </div>
        )}

        {gateway.capacity && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Devices:</span>
            <span className="font-medium text-gray-900">
              {gateway.capacity.connectedSensors || 0} / {gateway.capacity.maxSensors || '?'}
            </span>
          </div>
        )}

        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Updated: {new Date(gateway.updatedAt).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}

